import { DrawerCloseButton, RegPlate, SharedEmpty, SharedSpinner, VehicleDetailsCard } from '@components';
import { useView } from '@hooks';
import { Vehicle } from '@services';
import { useStore } from '@stores';
import { Drawer, Typography } from 'antd';
import React, { useEffect, useState } from 'react';

const { Text } = Typography;

interface IProps {
  open: boolean;
  vehicle: Vehicle | undefined;
  onClose: () => void;
  /**
   * Shows the "Not your vehicle? enter VIN" refresh, which re-reads the vehicle from UKVD (a
   * metered lookup). On by default: wherever these details are shown, a wrong plate-to-VIN match
   * is corrected the same way.
   */
  showRefresh?: boolean;
  /** Lets a host holding its own copy of the vehicle take the refreshed one. */
  setSelectedVehicle?: (vehicle: Vehicle | null) => void;
  /**
   * For hosts that only know the vehicle's identifiers (an order): with no `vehicle`, the drawer
   * looks it up by VIN - or by plate when the order carries no VIN - once it is opened.
   */
  vin?: string | null;
  registrationNumber?: string | null;
  /** Shown in the title while the lookup runs. */
  makeAndModel?: string;
}

/**
 * Everything we hold on a vehicle, opened from the short vehicle summary on the customer card.
 * The order screen used to give this its own tab; a drawer keeps the tab bar for the job itself.
 */
export const VehicleDetailsDrawer: React.FC<IProps> = ({
  open,
  vehicle: vehicleProp,
  onClose,
  showRefresh = true,
  setSelectedVehicle,
  vin,
  registrationNumber,
  makeAndModel: makeAndModelProp,
}) => {
  const { isMobile } = useView();
  const { vehicleStore } = useStore();
  const [fetched, setFetched] = useState<Vehicle | undefined>();
  // Set once the lookup has finished, so "not found" is not shown in the render before it starts.
  const [attempted, setAttempted] = useState(false);

  // A vehicle built from an order has no id: it only seeds the title, the record is fetched on open.
  const hasFullVehicle = !!vehicleProp?.id;
  const lookupKey = vin || registrationNumber;
  const needsLookup = open && !hasFullVehicle && !!lookupKey;
  useEffect(() => {
    setFetched(undefined);
    setAttempted(false);
    if (!needsLookup) return undefined;
    let cancelled = false;
    vehicleStore
      .getByVinOrRegistration(vin, registrationNumber)
      .then((result) => !cancelled && setFetched(result))
      .catch(() => undefined)
      .finally(() => !cancelled && setAttempted(true));
    return () => {
      cancelled = true;
    };
  }, [needsLookup, lookupKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const fetching = needsLookup && !attempted;

  const fullVehicle = hasFullVehicle ? vehicleProp : fetched;
  const vehicle = fullVehicle ?? vehicleProp;
  const plate = vehicle?.registrationNumber ?? registrationNumber;
  const makeAndModel =
    `${vehicle?.combinedMake ?? ''} ${vehicle?.combinedModel ?? ''}`.trim() ||
    (makeAndModelProp ?? '');

  return (
    <Drawer
      // The plate and model identify the drawer, so the body need not repeat them.
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          {plate && <RegPlate>{plate}</RegPlate>}
          {makeAndModel && (
            <Text
              type="secondary"
              style={{
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {makeAndModel}
            </Text>
          )}
        </span>
      }
      open={open}
      onClose={onClose}
      styles={{ wrapper: { width: isMobile ? '100%' : 800 } }}
      destroyOnHidden
      extra={<DrawerCloseButton onClick={onClose} />}
      closable={false}
    >
      {/* Two label/value pairs per row (one on a phone): the drawer is narrow, so the grid grows
          downwards rather than squeezing four pairs into a line. */}
      <SharedSpinner loading={fetching}>
        {fullVehicle ? (
          <VehicleDetailsCard
            vehicle={fullVehicle}
            showRefresh={showRefresh}
            setSelectedVehicle={(v) => {
              setFetched(v ?? undefined);
              setSelectedVehicle?.(v);
            }}
            bordered={false}
            hideHeader
            descriptionColumns={isMobile ? 1 : 2}
            descriptionLabelWidth={150}
          />
        ) : (
          !fetching && lookupKey && <SharedEmpty description="No vehicle details found" />
        )}
      </SharedSpinner>
    </Drawer>
  );
};
